import React, { useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, CircularProgress, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { capitalizeAll, capitalizeFirst } from '@moderepo/mode-app-components';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCInvestigateIssueContainer } from './RCInvestigateIssueContainer';
import { RCIssuePreviewCompOptions, RCSlideOutPanelCompType } from './RCProjectConfig';
import { useGetURLParamEntityIdAsNumber } from '../../utils/utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        actionsBar: {
            display       : 'flex',
            flexDirection : 'row',
            justifyContent: 'flex-end',
            alignItems    : 'center',
        },

        action: {
            '&:not(:first-child)': {
                marginLeft: theme.spacing(1),
            },
        },

        [theme.breakpoints.up('md')]: {
            action: {
                '&:not(:first-child)': {
                    marginLeft: theme.spacing(2),
                },
            },
        },
    };
});


export enum DataViewType {
    HISTORICAL = 'historical',
    REPLAY = 'replay'
}


/**
 * This page Display ALL the Project's issues or Health status
 */
export const RCInvestigateIssuePage = () => {

    // Get the params from the route or url
    const navigate = useNavigate();
    const params = useParams();
    const projectId = Number(params.projectId);
    const issueId = Number(params.issueId);
    const userId = useGetURLParamEntityIdAsNumber('userId');
    const homeId = useGetURLParamEntityIdAsNumber('homeId');
    const deviceId = useGetURLParamEntityIdAsNumber('deviceId');

    const { state:dataState } = useContext(ritzDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);

    const classes = useStyle();



    /**
     * Handler for the "Show Issue Details" button click. When these are clicked, we will show these in the Preview Panel
     */
    const onShowIssueDetailsClicked = useCallback(() => {
        const options: RCIssuePreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.ISSUE_PREVIEW,
            compProps: {
                data   : { projectId, issueId },
                context: { userId, homeId, deviceId },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [issueId, userId, homeId, deviceId, projectId, uiDispatch]);



    const onGoBackClick = useCallback(() => {
        navigate(-1);
    }, [navigate]);



    return (
        <div className={clsx('page-main-content')} style={{ paddingBottom: 200 }}>
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.investigate_issue')}
                    </div>
                    <div className="page-subtitle">
                        {capitalizeAll(
                            trans('page.subtitle.investigate_issue', { issue_id: issueId }),
                        )}
                    </div>
                </div>
                <div className={clsx(classes.actionsBar)}>
                    <Button
                        className={clsx(classes.action)}
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={onShowIssueDetailsClicked}
                    >
                        {capitalizeFirst(trans('label.show_issue_details'))}
                    </Button>
                    <Button
                        className={clsx(classes.action)}
                        variant="contained"
                        size="small"
                        onClick={onGoBackClick}
                    >
                        {capitalizeFirst(trans('label.back'))}
                    </Button>
                </div>
            </div>

            {project && (
                <RCInvestigateIssueContainer
                    projectId={projectId}
                    issueId={issueId}
                />
            )}

            {!project && (
                <CircularProgress />
            )}
        </div>
    );
};
